import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { TeamMember } from '../../types/teamSummary';

import { ProfileStatus } from '../../types/profile';

interface RowActionMenuProps {
  member: TeamMember;
  onRemove: (event: React.MouseEvent, member: TeamMember) => void;
  onSuspend: (event: React.MouseEvent, member: TeamMember) => void;
  onActivate: (event: React.MouseEvent, member: TeamMember) => void;
  onViewActivityLog: (event: React.MouseEvent, member: TeamMember) => void;
}

export function RowActionMenu({ 
  member, 
  onRemove, 
  onSuspend,
  onActivate,
  onViewActivityLog
}: RowActionMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, right: 0 });

  const handleButtonClick = () => {
    if (!isOpen && buttonRef.current) {
      // Calculate position synchronously before opening
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 4,
        right: window.innerWidth - rect.right
      });
    }
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current && 
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      // Recalculate position on scroll/resize
      const updatePosition = () => {
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          setPosition({
            top: rect.bottom + window.scrollY + 4,
            right: window.innerWidth - rect.right
          });
        }
      };

      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      document.addEventListener('mousedown', handleClickOutside);
      
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const dropdownContent = isOpen && (
    <div 
      ref={menuRef}
      className="fixed w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] py-1"
      style={{
        top: `${position.top}px`,
        right: `${position.right}px`
      }}
    >
      {/* TODO: Uncomment when View Activity Log feature is implemented
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onViewActivityLog(e, member);
          setIsOpen(false);
        }}
        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
      >
        View Activity Log
      </button>
      <div className="border-t border-gray-200 my-1"></div>
      */}
      {member.status === ProfileStatus.Suspended ? (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onActivate(e, member);
            setIsOpen(false);
          }}
          className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50 transition-colors"
        >
          Activate
        </button>
      ) : (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onSuspend(e, member);
            setIsOpen(false);
          }}
          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
        >
          Suspend
        </button>
      )}
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRemove(e, member);
          setIsOpen(false);
        }}
        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
      >
        Remove
      </button>
    </div>
  );

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleButtonClick}
        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        aria-label="Actions"
      >
        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
        </svg>
      </button>
      {typeof document !== 'undefined' && dropdownContent && createPortal(dropdownContent, document.body)}
    </>
  );
}

